import Const from 'constants';
import NumberHelper from 'helpers/number-helper';
import StackItemInterface from 'components/organisms/correlation-stacking/interfaces/stack-item-interface';
import StackPlayerInterface from 'components/organisms/correlation-stacking/interfaces/stack-player-interface';
import {generateSignature, generateRandomKey} from 'helpers/hash';

function preparePlayerPoints(pointsList, useCoefficient) {
    const {adminPoints, ceilingPoints} = pointsList;

    let points = useCoefficient ? ceilingPoints : adminPoints;

    if (useCoefficient) {
        points *= Const.CORRELATION_STACKING_BOOST_COEFFICIENT;
    }

    return NumberHelper.float(points, 3);
}

const universalStackCombinations = {
    [Const.SECONDARY_STACK_COMBINATION_TYPE_1]: true,
    [Const.SECONDARY_STACK_COMBINATION_TYPE_2]: true,
    [Const.SECONDARY_STACK_COMBINATION_TYPE_3]: false,
    [Const.SECONDARY_STACK_COMBINATION_TYPE_4]: true,
    [Const.SECONDARY_STACK_COMBINATION_TYPE_5]: false,
};

const defaultState = {
    data: {
        stackItems: {},
        universalStackCombinations: cp(universalStackCombinations),
        useUniversalRule: true,
    },
    enabled: false,
    signature: null,
    all() {
        return {...this.data};
    },
    getStateId() {
        return 'CorrelationStacking.Settings';
    },
    getItems() {
        return Object.values(this.data.stackItems);
    },
    hasItems() {
        return !isEmpty(this.data.stackItems);
    },
    getItem(itemId) {
        if (this.data.stackItems.hasOwnProperty(itemId)) {
            return this.data.stackItems[itemId];
        }
        return null;
    },
    isEnabled() {
        return Boolean(this.enabled);
    },
    isUniversalRuleUsed() {
        return Boolean(this.data.useUniversalRule);
    },
    getUniversalStackCombinations() {
        return {...this.data.universalStackCombinations};
    },
    getSignature() {
        return this.signature;
    },
};

class StackItem {
    constructor() {
        this.uid = generateRandomKey();
        this.correlationBoost = false;
        this.keyPlayerId = null;
        this.exposure = {min: ''};
        this.stackType = Const.CORRELATION_STACKING_TYPE_TEAM;
        this.firstTeamAtLeastPlayers = 1;
        this.firstTeamAtMostPlayers = 3;
        this.secondTeamAtLeastPlayers = 1;
        this.secondTeamAtMostPlayers = 3;
        this.stackPlayers = {};

        this.secondaryStackType = Const.CORRELATION_SECONDARY_STACK_TYPE_SINGLE;
        this.secondaryStackCombinations = {
            [Const.SECONDARY_STACK_COMBINATION_TYPE_1]: true,
            [Const.SECONDARY_STACK_COMBINATION_TYPE_2]: true,
            [Const.SECONDARY_STACK_COMBINATION_TYPE_3]: true,
            [Const.SECONDARY_STACK_COMBINATION_TYPE_4]: true,
            [Const.SECONDARY_STACK_COMBINATION_TYPE_5]: true,
        };
    }
}

export default function correlationStacking(state = defaultState, action) {
    switch (action.type) {
        case Const.CORRELATION_STACKING_SET_DATA_FROM_STORAGE:
            state = {...state};
            state.data = action.data;
            Object.keys(state.data.stackItems).map((itemId) => {
                const stackItem = state.data.stackItems[itemId];
                Object.keys(stackItem.stackPlayers).map((key) => {
                    stackItem.stackPlayers[key] = {
                        ...stackItem.stackPlayers[key],
                        ...StackPlayerInterface,
                    };
                });
                state.data.stackItems[itemId] = {...stackItem, ...StackItemInterface};
            });
            return state;

        case Const.CORRELATION_STACKING_SET_IS_USED:
            state = {...state};
            state.enabled = Boolean(action.use);
            return state;

        case Const.CORRELATION_STACKING_SET_UNIVERSAL_RULE:
            state = {...state};
            state.data.useUniversalRule = Boolean(action.checked);
            return state;

        case Const.CORRELATION_STACKING_ADD_ITEM:
            state = {...state};
            {
                const item = {...new StackItem(), ...StackItemInterface};
                state.data.stackItems[item.getId()] = item;
            }
            return state;

        case Const.CORRELATION_STACKING_SET_BOOST:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.correlationBoost = Boolean(action.checked);
                Object.values(item.stackPlayers).forEach((stackPlayer) => {
                    stackPlayer.points = preparePlayerPoints(stackPlayer.pointsList, item.getCorrelationBoost());
                });
            }
            return state;

        case Const.CORRELATION_STACKING_SET_MIN_EXPOSURE:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.exposure.min = action.value;
            }
            return state;

        case Const.CORRELATION_STACKING_SET_KEY_PLAYER_ID:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.keyPlayerId = action.keyPlayerId;
            }
            return state;

        case Const.CORRELATION_STACKING_SET_STACK_PLAYERS:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.stackPlayers = {};
                action.stackPlayers.map((stackPlayer) => {
                    stackPlayer.points = preparePlayerPoints(stackPlayer.pointsList, item.getCorrelationBoost());
                    item.stackPlayers[stackPlayer.id] = {
                        ...stackPlayer,
                        ...StackPlayerInterface,
                    };
                });
            }
            return state;

        case Const.CORRELATION_STACKING_PATCH_STACK_PLAYER:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                const player = item.stackPlayers[action.playerId];
                item.stackPlayers[action.playerId] = {
                    ...player,
                    ...action.data,
                };
            }
            return state;

        case Const.CORRELATION_STACKING_SET_FIRST_TEAM_AT_LEAST_PLAYERS:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.firstTeamAtLeastPlayers = Number(action.value);
            }
            return state;

        case Const.CORRELATION_STACKING_SET_FIRST_TEAM_AT_MOST_PLAYERS:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.firstTeamAtMostPlayers = Number(action.value);
            }
            return state;

        case Const.CORRELATION_STACKING_SET_SECOND_TEAM_AT_LEAST_PLAYERS:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.secondTeamAtLeastPlayers = Number(action.value);
            }
            return state;

        case Const.CORRELATION_STACKING_SET_SECOND_TEAM_AT_MOST_PLAYERS:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.secondTeamAtMostPlayers = Number(action.value);
            }
            return state;

        case Const.CORRELATION_STACKING_SET_STACK_TYPE:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.stackType = action.value;
            }
            return state;

        case Const.CORRELATION_STACKING_SET_SECONDARY_STACK_TYPE:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.secondaryStackType = Number(action.value);
            }
            return state;

        case Const.CORRELATION_STACKING_SET_SECONDARY_STACK_COMBINATION_TYPE:
            state = {...state};
            {
                const item = state.data.stackItems[action.itemId];
                item.secondaryStackCombinations[action.combinationType] = action.value;
            }
            return state;

        case Const.CORRELATION_STACKING_SET_UNIVERSAL_STACK_COMBINATION_TYPE:
            state = {...state};
            state.data.universalStackCombinations[action.combinationType] = action.value;

            return state;

        case Const.CORRELATION_STACKING_REMOVE_ITEM:
            state = {...state};
            delete state.data.stackItems[action.itemId];
            return state;

        case Const.RESET_ALL_SETTINGS:
        case Const.FILTER_BY_SLATE:
            return {
                ...defaultState,
                data: {
                    stackItems: {},
                    universalStackCombinations: cp(universalStackCombinations),
                    useUniversalRule: true,
                },
                enabled: false,
                signature: generateSignature(),
            };

        /**
         * Special cases when any another stacking gets active then
         * the current has to be turned off
         */
        case Const.TEAM_NAME_STACKING_SET_IS_USED:
        case Const.PLAYER_POSITION_STACKING_SET_IS_USED:
            state = {...state};
            if (Boolean(action.use)) {
                state.enabled = false;
            }
            return state;

        // case Const.SET_PROJECTION_POINTS_TYPE:
        //
        //     state = {...state};
        //     Object.values(state.data.stackItems).forEach(item => {
        //         Object.values(item.stackPlayers).forEach(stackPlayer => {
        //             stackPlayer.pointsList.defaultPoints = stackPlayer.pointsList[action.value];
        //             stackPlayer.points = preparePlayerPoints(
        //                 stackPlayer.pointsList.defaultPoints,
        //                 item.getCorrelationBoost()
        //             );
        //         });
        //     });
        //     return state;

        default:
            return state;
    }
}
